import React, { useEffect, useRef, useState } from 'react';
import { Mail, MapPin, Send, Linkedin, Github, ArrowUpRight } from 'lucide-react';
import { profileData } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';

const Contact = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission (mock)
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setSubmitted(true);
    setIsSubmitting(false);
    setFormData({ name: '', email: '', message: '' });
    
    setTimeout(() => setSubmitted(false), 3000);
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Column - Info */}
          <div>
            <div className="overflow-hidden">
              <p
                className={`text-gray-400 text-sm uppercase tracking-widest mb-4 transition-all duration-700 ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
                }`}
                style={{ fontFamily: 'Figtree, sans-serif' }}
              >
                Get In Touch
              </p>
            </div>
            <div className="overflow-hidden">
              <h2
                className={`text-4xl md:text-5xl font-light text-gray-900 mb-6 transition-all duration-700 delay-100 ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
                }`}
                style={{ fontFamily: 'Figtree, sans-serif' }}
              >
                Let's work
                <br />
                <span className="text-gray-400">together</span>
              </h2>
            </div>

            <p
              className={`text-gray-600 text-lg leading-relaxed mb-8 transition-all duration-700 delay-200 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
              style={{ fontFamily: 'Figtree, sans-serif' }}
            >
              Have a project in mind or just want to say hello? I'd love to hear from you.
              Let's create something amazing together.
            </p>

            {/* Contact Info */}
            <div
              className={`space-y-4 mb-8 transition-all duration-700 delay-300 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
            >
              <a
                href={`mailto:${profileData.email}`}
                className="flex items-center gap-3 text-gray-600 hover:text-gray-900 transition-colors duration-300 group"
              >
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center group-hover:bg-gray-200 transition-colors duration-300">
                  <Mail size={18} className="text-gray-500" />
                </div>
                <span style={{ fontFamily: 'Figtree, sans-serif' }}>{profileData.email}</span>
              </a>
              <div className="flex items-center gap-3 text-gray-600">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <MapPin size={18} className="text-gray-500" />
                </div>
                <span style={{ fontFamily: 'Figtree, sans-serif' }}>{profileData.location}</span>
              </div>
            </div>

            {/* Social Links */}
            <div
              className={`flex gap-4 transition-all duration-700 delay-400 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
            >
              <a
                href={profileData.github}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center hover:bg-gray-800 transition-all duration-300 hover:scale-110"
              >
                <Github size={20} />
              </a>
              <a
                href={profileData.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center hover:bg-gray-800 transition-all duration-300 hover:scale-110"
              >
                <Linkedin size={20} />
              </a>
            </div>
          </div>

          {/* Right Column - Form */}
          {/* <div
            className={`transition-all duration-700 delay-300 ${
              isVisible ? 'translate-x-0 opacity-100' : 'translate-x-12 opacity-0'
            }`}
          >
            <form onSubmit={handleSubmit} className="bg-gray-50 p-8 md:p-10 rounded-2xl">
              <div className="space-y-6">
                <div>
                  <Label
                    htmlFor="name"
                    className="text-gray-700 mb-2 block"
                    style={{ fontFamily: 'Figtree, sans-serif' }}
                  >
                    Name
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                    className="bg-white border-gray-200 focus:border-gray-400 transition-colors duration-300"
                    style={{ fontFamily: 'Figtree, sans-serif' }}
                  />
                </div>

                <div>
                  <Label
                    htmlFor="email"
                    className="text-gray-700 mb-2 block"
                    style={{ fontFamily: 'Figtree, sans-serif' }}
                  >
                    Email
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                    className="bg-white border-gray-200 focus:border-gray-400 transition-colors duration-300"
                    style={{ fontFamily: 'Figtree, sans-serif' }}
                  />
                </div>

                <div>
                  <Label
                    htmlFor="message"
                    className="text-gray-700 mb-2 block"
                    style={{ fontFamily: 'Figtree, sans-serif' }}
                  >
                    Message
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell me about your project..."
                    required
                    rows={5}
                    className="bg-white border-gray-200 focus:border-gray-400 transition-colors duration-300 resize-none"
                    style={{ fontFamily: 'Figtree, sans-serif' }}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gray-900 text-white py-6 hover:bg-gray-800 transition-all duration-300 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </span>
                  ) : submitted ? (
                    <span className="flex items-center gap-2">
                      Message Sent!
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Send Message
                      <Send size={18} />
                    </span>
                  )}
                </Button>
              </div>
            </form>
          </div> */}
        </div>
      </div>
    </section>
  );
};

export default Contact;
